import { useState, useEffect, useMemo } from "react";
import { useRegionAndLocationContext } from "../../../contexts/RegionAndLocationContext";
import { useUserContext } from "../../../contexts/UserContext";
import { NestedOption } from "../../../types";
import { formatStoredDateForDisplay } from "../../../utils/dateInputUtils";
import {
    assignmentsToNestedOptions,
    buildRegionDistrictLocationOptionGroups,
    formatNestedSelectionLabel,
    nestedOptionsToAssignments,
} from "../../../utils/regionDistrictOptions";
import { DateField, SearchSuggest } from "../../ui";
import { stripDanglingNoteStamp, withInsertedNoteStamp } from "../../../utils/unitNotes";
import { RegionDistrictNestedSelect } from "../RegionDistrictNestedSelect";
import "../../../css/UserProfileTabs.css";

export type ProfileFieldValue = string | boolean | string[];

interface EmploymentTabProps {
    user: any;
    isEditing: boolean;
    readOnly: boolean;
    onFieldChange?: (field: string, oldValue: ProfileFieldValue, newValue: ProfileFieldValue) => void;
}

interface EmploymentFormState {
    position: string;
    currentJobClassification: string;
    supervisor: string;
    dateOfHire: string;
    notesAboutUser: string;
}

const getInitialFormState = (user: any): EmploymentFormState => ({
    position: user?.position || "",
    currentJobClassification: user?.currentJobClassification || "",
    supervisor: user?.inputByPerson || "",
    dateOfHire: user?.dateOfHire || "",
    notesAboutUser: user?.notesAboutUser || "",
});

const formatRoleName = (role?: string | null): string => {
    if (!role) return "—";
    return role
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

const serializeAssignments = (values: string[]): string => JSON.stringify([...values].sort());

export const EmploymentTab = ({
    user,
    isEditing,
    readOnly,
    onFieldChange,
}: EmploymentTabProps) => {
    const { companyRegions, companyDistricts, companyLocations } = useRegionAndLocationContext();
    const { allCompanyUsers, companyUser, companyOwner } = useUserContext();
    const noteActorName = companyUser?.name || companyOwner?.name || "Unknown";
    const companyId = (user?.companyId as string) || "";

    const optionGroups = useMemo(
        () =>
            buildRegionDistrictLocationOptionGroups({
                companyId,
                companyRegions,
                companyDistricts,
                companyLocations,
            }),
        [companyId, companyRegions, companyDistricts, companyLocations],
    );

    const supervisorOptions = useMemo(() => {
        const currentUserId = user?.id as string | undefined;
        return allCompanyUsers
            .filter((candidate) => {
                if (!candidate?.name?.trim()) return false;
                if ((candidate as { archived?: boolean | null }).archived) return false;
                if (currentUserId && candidate.id === currentUserId) return false;
                return true;
            })
            .map((candidate) => {
                const name = candidate.name!.trim();
                return { value: name, label: name };
            })
            .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    }, [allCompanyUsers, user?.id]);

    const [form, setForm] = useState<EmploymentFormState>(getInitialFormState(user));
    const [selectedRegionOptions, setSelectedRegionOptions] = useState<NestedOption[]>([]);

    useEffect(() => {
        const initial = getInitialFormState(user);
        setForm(initial);
        setSelectedRegionOptions(
            assignmentsToNestedOptions(user, {
                companyId,
                companyRegions,
                companyDistricts,
                companyLocations,
            }),
        );
    }, [user, isEditing, companyId, companyRegions, companyDistricts, companyLocations]);

    const handleChange = (field: keyof EmploymentFormState, value: string) => {
        const oldValue = form[field] ?? "";
        setForm((prev) => ({ ...prev, [field]: value }));
        if (!onFieldChange || oldValue === value) return;

        // Supervisor UI label maps to User.inputByPerson
        if (field === "supervisor") {
            onFieldChange("inputByPerson", oldValue, value);
            return;
        }
        onFieldChange(field, oldValue, value);
    };

    const handleAssignmentsChange = (next: NestedOption[]) => {
        const previous = nestedOptionsToAssignments(selectedRegionOptions, {
            companyId,
            companyRegions,
            companyLocations,
        });
        const nextAssignments = nestedOptionsToAssignments(next, {
            companyId,
            companyRegions,
            companyLocations,
        });

        setSelectedRegionOptions(next);

        if (!onFieldChange) return;

        if (
            serializeAssignments(previous.regionalAssignment) !==
            serializeAssignments(nextAssignments.regionalAssignment)
        ) {
            onFieldChange(
                "regionalAssignment",
                previous.regionalAssignment,
                nextAssignments.regionalAssignment,
            );
        }
        if (
            serializeAssignments(previous.locationAssignment) !==
            serializeAssignments(nextAssignments.locationAssignment)
        ) {
            onFieldChange(
                "locationAssignment",
                previous.locationAssignment,
                nextAssignments.locationAssignment,
            );
        }
        if (previous.globalLocationPermission !== nextAssignments.globalLocationPermission) {
            onFieldChange(
                "globalLocationPermission",
                previous.globalLocationPermission,
                nextAssignments.globalLocationPermission,
            );
        }
    };

    const districtDisplayValue = formatNestedSelectionLabel(selectedRegionOptions);

    const renderField = (
        label: string,
        field: keyof EmploymentFormState,
        options?: { type?: string; placeholder?: string }
    ) => {
        const value = form[field] || "";
        const isDateField = options?.type === "date";

        if (isEditing && !readOnly) {
            if (isDateField) {
                return (
                    <div className="upt-field-row">
                        <span className="upt-field-label">{label}</span>
                        <DateField
                            className="upt-field-date"
                            value={value}
                            onChange={(next) => handleChange(field, next)}
                            ariaLabel={label}
                        />
                    </div>
                );
            }
            return (
                <div className="upt-field-row">
                    <span className="upt-field-label">{label}</span>
                    <input
                        className="upt-field-input"
                        type={options?.type || "text"}
                        value={value}
                        placeholder={options?.placeholder || `Enter ${label.toLowerCase()}`}
                        onChange={(e) => handleChange(field, e.target.value)}
                    />
                </div>
            );
        }

        if (isDateField) {
            const formatted = value ? formatStoredDateForDisplay(value) : "";
            const isEmpty = !formatted || formatted === "-";
            return (
                <div className="upt-field-row">
                    <span className="upt-field-label">{label}</span>
                    <span className={`upt-field-value ${isEmpty ? "empty" : ""}`}>
                        {isEmpty ? "Not provided" : formatted}
                    </span>
                </div>
            );
        }

        return (
            <div className="upt-field-row">
                <span className="upt-field-label">{label}</span>
                <span className={`upt-field-value ${!value ? "empty" : ""}`}>
                    {value || "Not provided"}
                </span>
            </div>
        );
    };

    return (
        <div className="upt-tab-container">
            <div className="upt-section">
                <h3 className="upt-section-title">Employment Details</h3>
                <div className="upt-field-list">
                    {renderField("Job Title", "position", { placeholder: "Enter job title" })}
                    {renderField("Job Classification", "currentJobClassification", {
                        placeholder: "Enter classification",
                    })}

                    <div className="upt-field-row">
                        <span className="upt-field-label">District</span>
                        {isEditing && !readOnly ? (
                            <RegionDistrictNestedSelect
                                optionGroups={optionGroups}
                                value={selectedRegionOptions}
                                onChange={handleAssignmentsChange}
                                placeholder="Select regions and districts"
                                ariaLabel="Regions and districts"
                            />
                        ) : (
                            <span className={`upt-field-value ${!districtDisplayValue ? "empty" : ""}`}>
                                {districtDisplayValue || "Not provided"}
                            </span>
                        )}
                    </div>

                    <div className="upt-field-row">
                        <span className="upt-field-label">Supervisor</span>
                        {isEditing && !readOnly ? (
                            <SearchSuggest
                                className="upt-field-suggest"
                                options={supervisorOptions}
                                value={form.supervisor}
                                onChange={(value) => handleChange("supervisor", value)}
                                placeholder="Type to find a supervisor..."
                                emptyMessage="No matching users"
                                clearable
                                ariaLabel="Supervisor"
                            />
                        ) : (
                            <span className={`upt-field-value ${!form.supervisor ? "empty" : ""}`}>
                                {form.supervisor || "Not provided"}
                            </span>
                        )}
                    </div>

                    {renderField("Hire Date", "dateOfHire", { type: "date" })}
                </div>
            </div>

            <div className="upt-section">
                <h3 className="upt-section-title">Additional Information</h3>
                <div className="upt-field-list">
                    <div className="upt-field-row">
                        <span className="upt-field-label">Role</span>
                        <span className="upt-field-value">{formatRoleName(user?.role)}</span>
                    </div>

                    <div className="upt-field-row">
                        <span className="upt-field-label">Account Status</span>
                        <span className="upt-field-value">
                            {user?.cognitoId ? "Confirmed" : "Unconfirmed"}
                        </span>
                    </div>

                    <div className="upt-field-row">
                        <span className="upt-field-label">Notes</span>
                        {isEditing && !readOnly ? (
                            <textarea
                                className="upt-field-input upt-field-textarea"
                                value={form.notesAboutUser}
                                placeholder="Notes about this employee"
                                rows={3}
                                onFocus={() => {
                                    const next = withInsertedNoteStamp(form.notesAboutUser, noteActorName);
                                    if (next !== form.notesAboutUser) handleChange("notesAboutUser", next);
                                }}
                                onBlur={() => {
                                    const next = stripDanglingNoteStamp(form.notesAboutUser);
                                    if (next !== form.notesAboutUser) handleChange("notesAboutUser", next);
                                }}
                                onChange={(e) => handleChange("notesAboutUser", e.target.value)}
                            />
                        ) : (
                            <span className={`upt-field-value ${!form.notesAboutUser ? "empty" : ""}`}>
                                {form.notesAboutUser || "No notes"}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
